import { ODataParams } from '@sensenet/client-core'
import { GenericContent } from '@sensenet/default-content-types'
import React, { createContext, Dispatch, FunctionComponent, useReducer } from 'react'

export interface LoadSettingsContextProps {
  /**
   * OData settings for the Current Content
   */
  loadSettings: ODataParams<GenericContent>
  /**
   * OData settings for loading the current content's children
   */
  loadChildrenSettings: ODataParams<GenericContent>
  /**
   * OData settings for loading ancestors
   */
  loadAncestorsSettings: ODataParams<GenericContent>

  /**
   * Sets the Current Content's load settings
   */
  setLoadSettings: Dispatch<ODataParams<GenericContent>>

  /**
   * Set the current content's children's load settings
   */
  setLoadChildrenSettings: Dispatch<ODataParams<GenericContent>>

  /**
   * Sets the current content's ancestor's load settings
   */
  setLoadAncestorsSettings: Dispatch<ODataParams<GenericContent>>
}

/**
 * Context that stores load settings for OData requests
 */
export const LoadSettingsContext = createContext<LoadSettingsContextProps>({
  setLoadSettings: () => undefined,
  setLoadChildrenSettings: () => undefined,
  setLoadAncestorsSettings: () => undefined,
  loadAncestorsSettings: {},
  loadChildrenSettings: {},
  loadSettings: {},
})
LoadSettingsContext.displayName = 'LoadSettingsContext'

const initialLoadChildrenSettings: ODataParams<GenericContent> = {
  orderby: [['DisplayName', 'asc']],
  select: 'all',
  expand: ['CreatedBy'],
}
/**
 * Provider for the LoadSettingsContext. Sets up default loading settings.
 */

const setLoadSettingsReducer = (state: ODataParams<GenericContent>, value: ODataParams<GenericContent>) => {
  if (JSON.stringify(value) === JSON.stringify(state)) {
    return state
  }
  return value
}

const setLoadChildrenSettingsReducer = (state: ODataParams<GenericContent>, value: ODataParams<GenericContent>) => {
  if (JSON.stringify(value) === JSON.stringify(state)) {
    return state
  }
  return value
}

export const LoadSettingsContextProvider: FunctionComponent<{
  loadChildrenSettings?: ODataParams<GenericContent>
}> = (props) => {
  const [loadSettings, setLoadSettings] = useReducer(setLoadSettingsReducer, {})
  const [loadChildrenSettings, setLoadChildrenSettings] = useReducer(
    setLoadChildrenSettingsReducer,
    props.loadChildrenSettings ?? initialLoadChildrenSettings,
  )
  const [loadAncestorsSettings, setLoadAncestorsSettings] = useReducer(setLoadSettingsReducer, {})

  return (
    <LoadSettingsContext.Provider
      value={{
        loadSettings,
        loadChildrenSettings,
        loadAncestorsSettings,
        setLoadSettings,
        setLoadChildrenSettings,
        setLoadAncestorsSettings,
      }}>
      {props.children}
    </LoadSettingsContext.Provider>
  )
}
